/**
 * Labstep
 *
 * @module services/protocol-value
 * @desc A service to deal with protocol value
 */

import type { ProtocolValue } from 'labstep-web/models/protocol-value.model';
import type { Resource } from 'labstep-web/models/resource.model';
import {
  canUpdateAmount,
  checkUnitBase,
  getAmounts,
  isAmountUpdated,
} from 'labstep-web/services/amount-unit.service';
import type { StrictIconProps } from 'semantic-ui-react/dist/commonjs/elements/Icon';
import { resourceItemImportService } from './resource-item-import.service';

export const evaluateProtocolValue = (
  protocolValue: ProtocolValue,
) => {
  const errorMessages = {
    noItem: 'Please select item',
    noAmount: 'Amount used is not specified',
    noItemAmount: 'Item Amount Remaining Unspecified',
    noItemUnit: 'Item Unit Unspecified',
    noUnit: 'Amount used Unit Unspecified',
    mismatchingUnits:
      'Mismatching units between the item and the inventory field',
    insufficientAmount: 'Insufficient Amount Remaining',
    updatedAmount: 'Amount used deducted from item',
    readyToUpdate: 'Click to update amount remaining',
  };

  let canUpdate = false;
  let content = '';
  let iconName: StrictIconProps['name'] = 'warning sign';
  let iconColor: StrictIconProps['color'] = 'orange';

  if (!protocolValue.resource_item) {
    content = errorMessages.noItem;
  } else if (!protocolValue.amount) {
    content = errorMessages.noAmount;
  } else if (!protocolValue.resource_item.amount) {
    content = errorMessages.noItemAmount;
  } else if (!protocolValue.resource_item.unit) {
    content = errorMessages.noItemUnit;
  } else if (!protocolValue.unit) {
    content = errorMessages.noUnit;
  } else if (
    !checkUnitBase(
      protocolValue.unit || '',
      protocolValue.resource_item.unit || '',
    )
  ) {
    content = errorMessages.mismatchingUnits;
  } else if (isAmountUpdated(protocolValue)) {
    iconName = 'check circle';
    iconColor = 'green';
    content = errorMessages.updatedAmount;
  } else if (!canUpdateAmount(protocolValue)) {
    content = errorMessages.insufficientAmount;
  } else {
    canUpdate = true;
    iconName = 'check circle';
    iconColor = 'yellow';
    content = errorMessages.readyToUpdate;
  }

  return {
    canUpdate,
    content,
    iconName,
    iconColor,
  };
};

export const getValidProtocolValues = (
  protocolValues: ProtocolValue[],
) => {
  const values = protocolValues
    .map((value) => {
      const { amountTobeUpdated, resourceItemAmount } =
        getAmounts(value);
      if (amountTobeUpdated !== null && resourceItemAmount !== null) {
        return {
          protocolValueId: value.guid,
          resourceItemId: value.resource_item!.guid,
          amountTobeUpdated,
          resourceItemAmount,
        };
      }
    })
    .filter((value) => value !== undefined);

  const resourceItemAmounts = values.reduce((map, item) => {
    if (!map.has(item.resourceItemId)) {
      map.set(item.resourceItemId, item.resourceItemAmount);
    }
    return map;
  }, new Map<string, number>());
  // Filter the values based on the remaining amounts in resourceItem
  const result = values.filter((item) => {
    const remainingAmount =
      resourceItemAmounts.get(item.resourceItemId) || 0;

    if (item.amountTobeUpdated <= remainingAmount) {
      // Deduct the item's amount from the remaining resourceItem amount
      resourceItemAmounts.set(
        item.resourceItemId,
        remainingAmount - item.amountTobeUpdated,
      );
      return true; // Keep the item
    }

    // Exclude the item if there's not enough remaining resourceItem amount
    return false;
  });

  return result;
};

const getResourceItemsData = (
  protocolValues: ProtocolValue[],
  resource: Resource,
) => {
  const items: any[] = [];

  const fieldsAndData =
    resourceItemImportService.getInitialFieldsAndData(
      resource,
      protocolValues.length,
    );

  const names = fieldsAndData.data
    .slice(1) // skip header row
    .map((row) => row.A)
    .filter((name) => name && name.trim() !== '');

  items.push(
    ...protocolValues.map((p, index) => ({
      resource_id: resource.id,
      protocol_value_origin_guid: p.guid,
      name: names[index] || '',
    })),
  );
  return items;
};

// export const createResourceItemsForProtocolValuesOutput = (
//   protocolValues: ProtocolValue[],
//   experimentId?: string,
//   protocolId?: string,
//   resources?: Resource[],
//   read?: APIReadAction,
//   refresh?: () => void,
// ) => {
//   const items: any[] = [];

//   if (resources && resources.length === 1) {
//     items.push(...getResourceItemsData(protocolValues, resources[0]));
//   } else if (
//     resources &&
//     resources.length > 1 &&
//     resources.length === protocolValues.length
//   ) {
//     resources.forEach((resource, index) => {
//       items.push(
//         ...getResourceItemsData([protocolValues[index]], resource),
//       );
//     });
//   }

//   if (items.length > 0) {
//     createBatch({
//       entityName: ResourceItem.entityName,
//       body: items,
//       options: {
//         onSuccess: () => {
//           if (read && (experimentId || protocolId)) {
//             read({
//               params: {
//                 [`${experimentId ? Experiment.entityName : Protocol.entityName}_id`]:
//                   experimentId ? experimentId : protocolId,
//               },
//             });
//           }
//           if (refresh) {
//             refresh();
//           }
//         },
//       },
//     });
//   }
// };

export const getNewResourceDisplayName = (
  resourceTemplate?: Resource | null,
  incrementNumber: number = 1,
) => {
  if (!resourceTemplate) {
    return '';
  }
  const name = resourceTemplate.name;

  const customIdentifierSet = resourceTemplate.custom_identifier_set;
  const customIdentifierString = `${customIdentifierSet?.settings.prefix} ${(customIdentifierSet?.number_auto_increment ?? 0) + incrementNumber}`;

  return name !== ''
    ? `${name} ${customIdentifierString}`
    : customIdentifierString;
};
